// src/features/tasks/components/TaskDetailsPage/TaskInfoCard/TaskFacts.jsx
// One compact row of the task's key facts (priority, category, people) shown
// under the status bar, plus buttons to attach equipment/manuals when the task
// has none yet (the cards for those only appear once there is something to show).
import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPriorityColor } from '../../../utils/taskStateUtils';
import StaffMember from '../../../../../components/common/StaffMember';
import CategoryChip from '../../../../../components/common/CategoryChip';

function Fact({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3, minHeight: 28 }}>
        {children}
      </Box>
    </Box>
  );
}

export function TaskFacts({ task, onAddEquipment, onAddManual }) {
  const { t } = useTranslation(['common', 'tasks']);
  const assignees = [task.primary_assignee, task.secondary_assignee].filter((a) => a?.name);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', columnGap: 8, rowGap: 4 }}>
      <Fact label={t('tasks:labels.priority')}>
        <Chip size="small" label={t(`tasks:priority.${task.priority.toLowerCase()}`)} color={getPriorityColor(task.priority)} />
      </Fact>

      <Fact label={t('tasks:labels.category')}>
        {task.category?.name ? (
          <CategoryChip name={task.category.name} color={task.category.color_code} />
        ) : (
          <Typography variant="body2" color="text.secondary">{t('tasks:messages.no_category')}</Typography>
        )}
      </Fact>

      <Fact label={t('tasks:labels.assignees')}>
        {assignees.length > 0 ? (
          assignees.map((a, i) => (
            <StaffMember
              key={a.staff_member_id || i}
              staffMember={a}
              avatarSize={28}
              nameVariant="body2"
              nameSx={{ fontWeight: 500 }}
              bgcolor={i === 0 ? 'primary.main' : 'secondary.main'}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">{t('tasks:messages.no_assignees')}</Typography>
        )}
      </Fact>

      {(onAddEquipment || onAddManual) && (
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
          {onAddEquipment && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddEquipment}>
              {t('tasks:equipment.add_equipment')}
            </Button>
          )}
          {onAddManual && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddManual}>
              {t('tasks:manuals.add')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export default TaskFacts;
